import { ReactNode, useCallback, useRef, useState } from 'react';

import Popover, {
  InstanceRef,
} from '@decisiv/ui-components/lib/components/Popover';
import Tag, { TagProps } from '@decisiv/ui-components/lib/components/Tag';
import isEmpty from 'lodash/isEmpty';

import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';

import { Container } from './styles';

export interface OverflowTagItem {
  key: string;
  text: ReactNode;
}

interface OverflowTagListProps {
  items: OverflowTagItem[];
  /** Rendered when there are no items at all. */
  emptyPlaceholder?: ReactNode;
  /** `aria-label` for the count tag, built from the number of hidden items. */
  overflowAriaLabel?: (hiddenCount: number) => string;
  /** Extra props forwarded to every `Tag` (palette overrides, etc.). */
  tagProps?: Partial<TagProps>;
}

/**
 * Renders the first item inline and collapses everything after it behind a
 * `+N` tag that opens a popover with the remaining items.
 */
export default function OverflowTagList({
  items,
  emptyPlaceholder = '-',
  overflowAriaLabel = (hiddenCount) => `Show ${hiddenCount} more`,
  tagProps,
}: OverflowTagListProps) {
  const actionRef = useRef(null);
  const [popover, setPopover] = useState<InstanceRef | null>(null);
  const popoverRef = useCallback(
    (instance: InstanceRef | null) => setPopover(instance),
    [],
  );

  if (isEmpty(items)) {
    if (!emptyPlaceholder) {
      return null;
    }

    return (
      <span data-testid="overflow-tag-list-empty">{emptyPlaceholder}</span>
    );
  }

  const [firstItem, ...hiddenItems] = items;

  const renderTag = ({ key, text }: OverflowTagItem) => (
    <Tag key={key} size="small" variant="outline" text={text} {...tagProps} />
  );

  if (isEmpty(hiddenItems)) {
    return <Container>{renderTag(firstItem)}</Container>;
  }

  return (
    <>
      <Container>
        {renderTag(firstItem)}
        <Tag
          size="small"
          variant="outline"
          actionAttributes={{
            'aria-label': overflowAriaLabel(hiddenItems.length),
          }}
          text={`+${hiddenItems.length}`}
          action={() => popover?.toggle()}
          actionRef={actionRef}
          actionIcon={ChevronDown}
          {...tagProps}
        />
      </Container>
      <Popover
        ref={popoverRef}
        target={actionRef}
        manageEvents={false}
        placement="bottom-start"
        zIndex={3}
      >
        <Container
          flexDirection="column"
          padding={1}
          style={{ maxWidth: 'none', flexWrap: 'nowrap' }}
        >
          {hiddenItems.map(renderTag)}
        </Container>
      </Popover>
    </>
  );
}
